import { Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

export interface ResetDemoPatientDialogProps {
  open: boolean;
  displayName: string;
  copyTag: string;
  isPending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

const TITLE_ID = 'reset-demo-patient-dialog-title';
const DESCRIPTION_ID = 'reset-demo-patient-dialog-description';
const COPY_TAG_ID = 'reset-demo-patient-dialog-copy-tag';

export function ResetDemoPatientDialog({
  open,
  displayName,
  copyTag,
  isPending,
  onConfirm,
  onOpenChange,
}: ResetDemoPatientDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending && !nextOpen) {
      return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-labelledby={TITLE_ID}
        aria-describedby={`${DESCRIPTION_ID} ${COPY_TAG_ID}`}
        showCloseButton={!isPending}
        className="border-red-500/60 bg-red-950/90 text-red-100 shadow-[0_0_40px_rgba(239,68,68,0.15)]"
      >
        <DialogHeader>
          <div className="flex items-start gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
              <TriangleAlert className="size-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="space-y-1.5">
              <DialogTitle id={TITLE_ID} className="text-red-50">
                Reset {displayName} to base?
              </DialogTitle>
              <DialogDescription id={DESCRIPTION_ID} className="text-red-300/80">
                This discards every conversation, care-state change, note, draft, lab and
                prescription record on this chart and restores {displayName} to base. Only your copy
                is affected; no other clinician sees this patient.
              </DialogDescription>
              {/* Every clinician owns an identical patient of this name, so the
                  headline alone cannot say which copy is about to be wiped. The
                  fixture slug would not narrow it either — all six copies share
                  one — and naming it here is what the confirm dialog was
                  deliberately moved away from. */}
              <p id={COPY_TAG_ID} className="font-mono text-sm text-red-300/80">
                copy {copyTag}
              </p>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="border-red-500/30 bg-red-900/60 text-red-200 hover:bg-red-800/60"
          >
            Keep as is
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            aria-busy={isPending}
            className="bg-red-600 text-white hover:bg-red-500"
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Resetting…
              </>
            ) : (
              'Reset to base'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
